// src/Navbar.js

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { Sun, Moon, QrCode, Menu, X, ChevronDown, LogOut, User } from 'lucide-react';

// ── Static configs ────────────────────────────────────────────────────────────
const navEnter   = { initial: { y: -64, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] } };
const menuAnim   = { initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: 'auto' }, exit: { opacity: 0, height: 0 }, transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] } };
const dropAnim   = { initial: { opacity: 0, y: -12, scale: 0.94 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -8, scale: 0.96 }, transition: { type: 'spring', stiffness: 400, damping: 25 } };
const iconEnter  = { initial: { rotate: -90, opacity: 0 }, animate: { rotate: 0, opacity: 1 }, exit: { rotate: 90,  opacity: 0 }, transition: { duration: 0.12 } };
const iconExit   = { initial: { rotate: 90,  opacity: 0 }, animate: { rotate: 0, opacity: 1 }, exit: { rotate: -90, opacity: 0 }, transition: { duration: 0.12 } };
const indicatorT = { type: 'spring', stiffness: 400, damping: 30 };

// ── Tab button ────────────────────────────────────────────────────────────────
const TabButton = memo(({ id, label, emoji, isActive, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(id)}
    className="relative px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-200 z-10 whitespace-nowrap"
  >
    {isActive && (
      <motion.div
        layoutId="nav-indicator"
        className="absolute inset-0 bg-white dark:bg-[#0f0f1a] rounded-lg shadow-sm"
        transition={indicatorT}
      />
    )}
    <span className={"relative z-10 flex items-center gap-1 " + (isActive
      ? "text-purple-600 dark:text-purple-400"
      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300")}>
      <span className="text-sm">{emoji}</span>
      <span className="hidden lg:inline">{label}</span>
    </span>
  </button>
));
TabButton.displayName = 'TabButton';

// ── Mobile menu item ──────────────────────────────────────────────────────────
const MobileMenuItem = memo(({ id, label, emoji, isActive, onClick, delay }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay }}
    onClick={() => onClick(id)}
    className={"w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors duration-200 " +
      (isActive
        ? "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20"
        : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a2e]")}
  >
    <span className="text-lg">{emoji}</span>
    <span>{label}</span>
    {isActive && <span className="ml-auto w-2 h-2 rounded-full bg-purple-500" />}
  </motion.button>
));
MobileMenuItem.displayName = 'MobileMenuItem';

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = memo(({ picture, name, size = 8 }) => {
  const [imgError, setImgError] = useState(false);
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const sizeClass = "w-" + size + " h-" + size;

  if (picture && !imgError) {
    return (
      <img
        src={picture}
        alt={name || 'User avatar'}
        onError={() => setImgError(true)}
        className={sizeClass + " rounded-full object-cover ring-2 ring-purple-500/30 flex-shrink-0"}
      />
    );
  }

  return (
    <div className={sizeClass + " rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center ring-2 ring-purple-500/30 flex-shrink-0"}>
      <span className="text-white text-xs font-bold">{initials}</span>
    </div>
  );
});
Avatar.displayName = 'Avatar';

// ── User dropdown ─────────────────────────────────────────────────────────────
const UserDropdown = memo(({ user, onLogout, onClose }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <motion.div
      ref={dropdownRef}
      {...dropAnim}
      className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-[#1a1a2e] border border-gray-100 dark:border-purple-500/20 rounded-2xl shadow-xl overflow-hidden z-50"
    >
      {/* User info header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-purple-500/10 bg-gray-50 dark:bg-[#0f0f1a]/50">
        <div className="flex items-center gap-3">
          <Avatar picture={user.picture} name={user.name} size={9} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {user.name}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
              {user.email}
            </p>
          </div>
        </div>
      </div>

      <div className="p-2 space-y-0.5">
        {/* Email row */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl">
          <User size={13} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
          <span className="text-xs text-gray-400 dark:text-gray-500 truncate">{user.email}</span>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-purple-500/10 mx-1 my-1" />

        {/* Sign out */}
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
        >
          <LogOut size={13} aria-hidden="true" />
          Sign out
        </button>
      </div>
    </motion.div>
  );
});
UserDropdown.displayName = 'UserDropdown';

// ── Compact Google sign-in ────────────────────────────────────────────────────
const GoogleSignInButton = memo(({ onSuccess, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-[#1a1a2e] border border-gray-200 dark:border-purple-500/20">
        <div className="w-3.5 h-3.5 rounded-full border-2 border-purple-500/30 border-t-purple-500 animate-spin flex-shrink-0" />
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">Signing in...</span>
      </div>
    );
  }

  return (
    // Constrain the Google button size so it fits the navbar
    <div
      style={{ transform: 'scale(0.85)', transformOrigin: 'center' }}
      className="flex-shrink-0"
    >
      <GoogleLogin
        onSuccess={onSuccess}
        onError={() => console.error('Google login failed')}
        useOneTap={false}
        theme="filled_black"
        size="medium"
        shape="pill"
        text="signin_with"
        locale="en"
      />
    </div>
  );
});
GoogleSignInButton.displayName = 'GoogleSignInButton';

// ── Auth section ──────────────────────────────────────────────────────────────
const AuthSection = memo(({ compact = false }) => {
  const { user, login, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);

  const handleSuccess = useCallback((credentialResponse) => {
    setIsLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      login({
        name:    decoded.name    || 'User',
        email:   decoded.email   || '',
        picture: decoded.picture || '',
        sub:     decoded.sub     || '',
      });
    } catch (err) {
      console.error('Failed to decode Google credential:', err);
    }
    setIsLoading(false);
  }, [login]);

  const handleLogout   = useCallback(() => { logout(); setDropdownOpen(false); }, [logout]);
  const closeDropdown  = useCallback(() => setDropdownOpen(false), []);
  const toggleDropdown = useCallback(() => setDropdownOpen(p => !p), []);

  if (!user) {
    return <GoogleSignInButton onSuccess={handleSuccess} isLoading={isLoading} />;
  }

  // Compact version for mobile menu
  if (compact) {
    return (
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-3">
          <Avatar picture={user.picture} name={user.name} size={8} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user.name}</p>
            <p className="text-xs text-gray-400 truncate">{user.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 flex-shrink-0"
        >
          <LogOut size={12} aria-hidden="true" />
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={toggleDropdown}
        aria-label="Open user menu"
        aria-expanded={dropdownOpen}
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200 hw-accel"
      >
        <Avatar picture={user.picture} name={user.name} size={7} />
        <span className="hidden xl:block text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[80px] truncate">
          {user.name.split(' ')[0]}
        </span>
        <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 25 }} className="text-gray-400 flex-shrink-0">
          <ChevronDown size={13} aria-hidden="true" />
        </motion.div>
      </button>

      <AnimatePresence>
        {dropdownOpen && (
          <UserDropdown user={user} onLogout={handleLogout} onClose={closeDropdown} />
        )}
      </AnimatePresence>
    </div>
  );
});
AuthSection.displayName = 'AuthSection';

// ── Main Navbar ───────────────────────────────────────────────────────────────
const Navbar = memo(({ activeTab, onTabChange, onLogoClick, onStart, isLanding = false }) => {
  const { isDark, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 768) setMenuOpen(false); };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabClick = useCallback((tabId) => {
    onTabChange(tabId);
    setMenuOpen(false);
  }, [onTabChange]);

  const handleLogoClick = useCallback(() => {
    if (onLogoClick) onLogoClick();
    setMenuOpen(false);
  }, [onLogoClick]);

  const toggleMenu = useCallback(() => setMenuOpen(p => !p), []);

  const navBg = scrolled || !isLanding
    ? "bg-white/80 dark:bg-[#0f0f1a]/90 backdrop-blur-xl border-b border-gray-100 dark:border-purple-500/10 shadow-sm"
    : "bg-transparent";

  return (
    <>
      <motion.nav
        {...navEnter}
        role="navigation"
        aria-label="Main navigation"
        className={"fixed top-0 left-0 right-0 z-50 transition-colors duration-200 " + navBg}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-3">

            {/* ── Logo ── */}
            <button
              onClick={handleLogoClick}
              aria-label="Qronetic — go to homepage"
              className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-lg px-1 flex-shrink-0 mr-2"
            >
              <QrCode size={20} className="text-purple-600 dark:text-purple-400" aria-hidden="true" />
              <span className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">
                Qronetic
              </span>
              <span className="hidden sm:inline-block text-xs px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-semibold">
                FREE
              </span>
            </button>

            {/* ── Desktop tab strip — fills remaining space ── */}
            <div className="hidden md:flex flex-1 items-center justify-center">
              <div className="flex items-center gap-0.5 bg-gray-100/80 dark:bg-[#1a1a2e]/80 backdrop-blur-sm p-1 rounded-xl border border-gray-200 dark:border-purple-500/10">
                {!isLanding ? (
                  <>
                    <TabButton id="main"      label="Generate & Scan" emoji="⬡"  isActive={activeTab === 'main'}      onClick={handleTabClick} />
                    <TabButton id="utilities" label="Utilities"        emoji="⚡" isActive={activeTab === 'utilities'} onClick={handleTabClick} />
                    <TabButton id="history"   label="History"          emoji="📋" isActive={activeTab === 'history'}   onClick={handleTabClick} />
                    <TabButton id="contact"   label="Contact"          emoji="✉️" isActive={activeTab === 'contact'}   onClick={handleTabClick} />
                    <TabButton id="about"     label="About"            emoji="ℹ️" isActive={activeTab === 'about'}     onClick={handleTabClick} />
                    <TabButton id="privacy"   label="Privacy"          emoji="🔒" isActive={activeTab === 'privacy'}   onClick={handleTabClick} />
                  </>
                ) : (
                  <>
                    <TabButton id="about"   label="About"   emoji="ℹ️" isActive={false} onClick={handleTabClick} />
                    <TabButton id="contact" label="Contact" emoji="✉️" isActive={false} onClick={handleTabClick} />
                    <TabButton id="privacy" label="Privacy" emoji="🔒" isActive={false} onClick={handleTabClick} />
                  </>
                )}
              </div>
            </div>

            {/* ── Right controls ── */}
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">

              {/* Get Started — landing only */}
              {isLanding && (
                <button
                  onClick={onStart}
                  aria-label="Get started with Qronetic"
                  className="hidden sm:block px-3 py-1.5 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors duration-200 whitespace-nowrap"
                >
                  Get Started →
                </button>
              )}

              {/* Google Auth — desktop */}
              <div className="hidden md:flex items-center">
                <AuthSection />
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                className="p-2 rounded-xl bg-white/20 dark:bg-[#1a1a2e]/60 backdrop-blur-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300 hover:border-purple-400/40 transition-colors duration-200 flex-shrink-0"
              >
                {isDark
                  ? <Sun  size={16} className="text-yellow-400" aria-hidden="true" />
                  : <Moon size={16} className="text-indigo-500" aria-hidden="true" />
                }
              </button>

              {/* Hamburger */}
              <button
                onClick={toggleMenu}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                className="md:hidden p-2 rounded-xl bg-gray-100 dark:bg-[#1a1a2e] border border-gray-200 dark:border-purple-500/20 text-gray-600 dark:text-gray-300 transition-colors duration-200 flex-shrink-0"
              >
                <AnimatePresence mode="wait">
                  {menuOpen
                    ? <motion.div key="x"    {...iconEnter}><X    size={16} aria-hidden="true" /></motion.div>
                    : <motion.div key="menu" {...iconExit} ><Menu size={16} aria-hidden="true" /></motion.div>
                  }
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile dropdown ── */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              id="mobile-menu"
              role="menu"
              aria-label="Mobile navigation"
              {...menuAnim}
              className="md:hidden overflow-hidden border-t border-gray-100 dark:border-purple-500/10 bg-white/95 dark:bg-[#0f0f1a]/95 backdrop-blur-xl"
            >
              <div className="px-4 py-3 space-y-1">

                {!isLanding ? (
                  <>
                    <MobileMenuItem id="main"      label="Generate & Scan" emoji="⬡"  isActive={activeTab === 'main'}      onClick={handleTabClick} delay={0}    />
                    <MobileMenuItem id="utilities" label="Utilities"        emoji="⚡" isActive={activeTab === 'utilities'} onClick={handleTabClick} delay={0.04} />
                    <MobileMenuItem id="history"   label="History"          emoji="📋" isActive={activeTab === 'history'}   onClick={handleTabClick} delay={0.08} />
                    <MobileMenuItem id="contact"   label="Contact"          emoji="✉️" isActive={activeTab === 'contact'}   onClick={handleTabClick} delay={0.12} />
                    <MobileMenuItem id="about"     label="About"            emoji="ℹ️" isActive={activeTab === 'about'}     onClick={handleTabClick} delay={0.16} />
                    <MobileMenuItem id="privacy"   label="Privacy Policy"   emoji="🔒" isActive={activeTab === 'privacy'}   onClick={handleTabClick} delay={0.20} />
                  </>
                ) : (
                  <>
                    <MobileMenuItem id="about"   label="About"          emoji="ℹ️" isActive={false} onClick={handleTabClick} delay={0}    />
                    <MobileMenuItem id="contact" label="Contact"        emoji="✉️" isActive={false} onClick={handleTabClick} delay={0.04} />
                    <MobileMenuItem id="privacy" label="Privacy Policy" emoji="🔒" isActive={false} onClick={handleTabClick} delay={0.08} />
                  </>
                )}

                {/* Auth — compact version for mobile */}
                <div className="pt-2 mt-1 border-t border-gray-100 dark:border-purple-500/10">
                  <AuthSection compact={true} />
                </div>

                {/* Get started — landing mobile */}
                {isLanding && (
                  <button
                    onClick={() => { if (onStart) onStart(); setMenuOpen(false); }}
                    className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors duration-200"
                  >
                    Get Started Free →
                  </button>
                )}

                <div className="pt-2 border-t border-gray-100 dark:border-purple-500/10">
                  <p className="text-xs text-gray-400 dark:text-gray-600 text-center py-1">
                    Free · Private · No sign-up required
                  </p>
                </div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {!isLanding && <div className="h-16" aria-hidden="true" />}
    </>
  );
});

Navbar.displayName = 'Navbar';
export default Navbar;